import React from 'react';
import { ACL_RESOURCE_TYPE_CREDENTIALS } from 'js//constants/aclConstants';
import CredentialsTable from 'js/components/credentials/CredentialsTable';
import AclTabViewContainer from 'js/components/elements/aclView/AclTabViewContainer';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import ResourceEditProvider from 'js/components/ResourceEditProvider';
import { ResourceEditContext } from 'js/components/ResourceEditProvider/ResourceEditContext';
import { CONTACT_SUPPORT_LINK } from 'js/constants/config';
import { RESOURCE_TYPE_CREDENTIALS } from 'js/constants/resourceTypes';
import { SUPPORT_KEY_CREDENTIAL } from 'js/constants/supportPageKeys';
import { AvailableFilterTypesByView, SearchFilterViews } from 'js/constants/visibilityFilters';
import { isEmpty } from 'lodash';
import RoutePrefixContext from 'new/contexts/RoutePrefixContext';
import { BannerDescription } from 'new/elements/BannerDescription';
import { createCredentialTabConfigs } from 'new/components/DetailViewTabs/Credential/config/credential.tabs';
import CredentialOverviewTabContainer from './cards/CredentialOverviewTabContainer';
import { useRestrictedTiers } from 'new/hooks/useRestrictedTiers';

import './ViewCredentials.scss';

const description =
  'Credentials are required to integrate to Sources and Destinations. Multiple Sources and Destinations can share the same credentials. You can create them here or when creating Sources or Destinations.';

class ViewCredentials extends React.Component {
  static contextType = RoutePrefixContext;

  componentDidMount() {
    this.props.setContextualSupport(SUPPORT_KEY_CREDENTIAL);
  }

  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_CREDENTIAL);
  }

  openCredentialModal = () => {
    this.props.setAction({
      type: 'create',
      resource: RESOURCE_TYPE_CREDENTIALS,
    });
  };

  render() {
    const {
      isLoading,
      credentials,
      credentialId,
      getSourceMeta,
      loadDataCredentialListing,
      hasExpressTierLimitedAccess,
    } = this.props;

    const credentialDetailViewTabs = createCredentialTabConfigs({
      includeUsage: true,
      includeAccess: true,
    });

    const credentialDetailViewTabsWithProps = credentialDetailViewTabs.map((tab) => {
      if (tab.value === 'usage') {
        return {
          ...tab,
          tabElement: React.cloneElement(tab.tabElement, { credentialId }),
        };
      }
      return tab;
    });

    let noContentView = null;
    if (!isLoading && isEmpty(credentials)) {
      if (credentialId) {
        noContentView = (
          <NoContentTextView
            title={<div>We are sorry, we could not find this credential.</div>}
            subtitle={
              <div>
                Please reload the page or file a{' '}
                <a style={{ color: '#507fe7' }} href={CONTACT_SUPPORT_LINK}>
                  support ticket
                </a>{' '}
                to request support.
              </div>
            }
          />
        );
      } else {
        noContentView = (
          <NoContentTextView
            title={
              <div>
                Add your first credential! Click on the Create New Credential button above to get
                started.
              </div>
            }
            subtitle={
              <div>
                File a{' '}
                <a style={{ color: '#507fe7' }} href={CONTACT_SUPPORT_LINK}>
                  support ticket
                </a>{' '}
                to request support for credential creation.
              </div>
            }
          />
        );
      }
    }

    return (
      <div>
        <BannerDescription
          title="Credentials"
          description={description}
          {...(!hasExpressTierLimitedAccess && {
            actionTitle: 'New Credential',
            onClick: this.openCredentialModal,
          })}
        />
        <div className="view-credentials">
          <CredentialsTable
            view={SearchFilterViews.CREDENTIALS}
            urlId={credentialId}
            isLoading={isLoading}
            data={credentials}
            detailViewTabs={credentialDetailViewTabsWithProps}
            getSourceMeta={getSourceMeta}
            availableFilterTypes={AvailableFilterTypesByView[SearchFilterViews.CREDENTIALS]}
            noContentView={noContentView}
            loadData={loadDataCredentialListing}
          />
        </div>
      </div>
    );
  }
}

const ViewCredentialsWrapped = (props) => {
  const { hasExpressTierLimitedAccess } = useRestrictedTiers();

  return (
    <ResourceEditProvider>
      <ResourceEditContext.Consumer>
        {(consumerProps) => (
          <ViewCredentials
            {...consumerProps}
            {...props}
            hasExpressTierLimitedAccess={hasExpressTierLimitedAccess}
          />
        )}
      </ResourceEditContext.Consumer>
    </ResourceEditProvider>
  );
};

export default ViewCredentialsWrapped;
